import {
  addDoc,
  collection,
  doc,
  getDocs,
  query,
  serverTimestamp,
  updateDoc,
  where,
} from "firebase/firestore";
import { db } from "../../firebase/config";
import {
  getTotalCashOutAvailable,
  maxCashOutWithdrawalRequest,
  validateCashOutCashWalletWithdrawalAmount,
  CASH_OUT_WITHDRAWAL_MIN,
} from "../../config/cashOutWithdrawalConfig";
import { listCashOutProgramsForUser } from "./cashOutProgramQueries";

async function userDocByUid(uid) {
  const snap = await getDocs(
    query(collection(db, "users"), where("uid", "==", uid))
  );
  if (snap.empty) return null;
  return { id: snap.docs[0].id, data: snap.docs[0].data() };
}

export async function getCashOutTransferToRoyaltyEligibility(uid) {
  const user = await userDocByUid(uid);
  if (!user) {
    return { eligible: false, reason: "User profile not found." };
  }

  const box = user.data.cashOutBox || { available: 0 };
  const wallet = user.data.cashOutCashWallet || { available: 0 };
  const available = getTotalCashOutAvailable(box, wallet);
  const maxTransfer = maxCashOutWithdrawalRequest(box, wallet);

  const programs = await listCashOutProgramsForUser(uid);
  const hasCop = programs.length > 0;

  if (!hasCop) {
    return {
      eligible: false,
      reason: "Enroll in Cashback on a qualifying cart order first.",
      available,
      maxTransfer,
    };
  }
  if (maxTransfer < CASH_OUT_WITHDRAWAL_MIN) {
    return {
      eligible: false,
      reason: `Minimum ₹${CASH_OUT_WITHDRAWAL_MIN} available to move to Cash Wallet.`,
      available,
      maxTransfer,
    };
  }

  return {
    eligible: true,
    available,
    maxTransfer,
  };
}

/**
 * Move COP earnings (box + cash wallet) into royaltyBalance for the existing
 * Cash → admin withdrawal flow.
 */
export async function transferCashOutEarningsToRoyalty({
  uid,
  userEmail,
  userName,
  amount,
}) {
  const eligibility = await getCashOutTransferToRoyaltyEligibility(uid);
  if (!eligibility.eligible) {
    throw new Error(eligibility.reason || "Not eligible");
  }

  const user = await userDocByUid(uid);
  const box = user.data.cashOutBox || { available: 0, withdrawn: 0, earned: 0 };
  const wallet = user.data.cashOutCashWallet || {
    available: 0,
    withdrawn: 0,
    earned: 0,
  };
  const check = validateCashOutCashWalletWithdrawalAmount(amount, box, wallet);
  if (!check.ok) {
    throw new Error(check.message);
  }

  const amt = Number(amount);
  let remaining = amt;
  const fromCash = Math.min(Number(wallet.available) || 0, remaining);
  remaining -= fromCash;
  const fromBox = remaining;

  const royaltyBefore = Number(user.data.royaltyBalance) || 0;

  await updateDoc(doc(db, "users", user.id), {
    cashOutCashWallet: {
      ...wallet,
      available: (Number(wallet.available) || 0) - fromCash,
      withdrawn: (Number(wallet.withdrawn) || 0) + fromCash,
    },
    cashOutBox: {
      ...box,
      available: (Number(box.available) || 0) - fromBox,
      withdrawn: (Number(box.withdrawn) || 0) + fromBox,
    },
    royaltyBalance: royaltyBefore + amt,
    updatedAt: new Date().toISOString(),
  });

  await addDoc(collection(db, "cashOutLedger"), {
    transactionType: "TRANSFER_TO_ROYALTY",
    userId: uid,
    amount: amt,
    fromWallet: "COP_EARNINGS",
    toWallet: "ROYALTY_BALANCE",
    reference: `COP → Cash`,
    status: "COMPLETED",
    metadata: { fromCash, fromBox, royaltyBefore, royaltyAfter: royaltyBefore + amt },
    createdAt: serverTimestamp(),
  });

  return {
    transferred: true,
    amount: amt,
    newRoyaltyBalance: royaltyBefore + amt,
    userEmail,
    userName,
  };
}

/** @deprecated use getCashOutTransferToRoyaltyEligibility */
export const getCashOutCashWalletWithdrawalEligibility =
  getCashOutTransferToRoyaltyEligibility;

/** @deprecated use transferCashOutEarningsToRoyalty */
export const submitCashOutCashWalletWithdrawal = transferCashOutEarningsToRoyalty;
